import React, { useState } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import { LogIn } from "lucide-react";

export default function LoginPage() {
  const { t } = useLanguage();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const redirectQuery = searchParams.get("redirect");
  const pkgQuery = searchParams.get("pkg");
  const from = redirectQuery
    ? redirectQuery + (pkgQuery ? `?pkg=${pkgQuery}` : "")
    : location.state?.from || "/dashboard";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success(t.auth.welcome);
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-md px-6 py-16" data-testid="login-page">
      <div className="hairline rounded-lg bg-card p-8 shadow-sm md:p-10">
        <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--gold))]">
          {t.auth.overline_login}
        </div>
        <h1 className="font-display mt-3 text-4xl font-bold tracking-tight">
          {t.auth.login_title}
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">
          {t.auth.login_subtitle}
        </p>

        <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
          <div className="space-y-2">
            <Label
              htmlFor="email"
              className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground"
            >
              {t.auth.email}
            </Label>
            <Input
              id="email"
              type="email"
              data-testid="login-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
              className="h-12"
            />
          </div>
          <div className="space-y-2">
            <Label
              htmlFor="password"
              className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground"
            >
              {t.auth.password}
            </Label>
            <Input
              id="password"
              type="password"
              data-testid="login-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              className="h-12"
            />
          </div>
          <Button
            type="submit"
            data-testid="login-submit-button"
            disabled={submitting}
            className="h-12 w-full rounded-md bg-[hsl(var(--gold))] font-semibold text-[hsl(var(--primary-foreground))] shadow-lg shadow-[hsl(var(--gold))]/20 hover:bg-[hsl(var(--gold-soft))]"
          >
            <LogIn className="mr-2 h-4 w-4" />
            {submitting ? t.common.loading : t.auth.login_cta}
          </Button>
        </form>
        <p className="mt-6 text-center text-sm text-muted-foreground">
          {t.auth.no_account}{" "}
          <Link
            to="/register"
            data-testid="login-goto-register"
            className="font-medium text-[hsl(var(--primary))] underline-offset-4 hover:underline"
          >
            {t.auth.register_cta}
          </Link>
        </p>
      </div>
    </div>
  );
}
